import { styled } from '@mui/material'
import { useMemo, useRef, useState, useEffect, cloneElement } from 'react'
import type { FC, ReactNode, ReactElement, CSSProperties } from 'react'
import { EViewMode } from './view-mode.types'

// Animation defaults and configuration for the view-mode switch.
// These values can be tuned here or overridden via the `animConfig` prop.
const DEFAULT_ANIM = {
  enter: 300,
  exit: 180,
  ease: 'cubic-bezier(0.25, 0.1, 0.25, 1)',
  delayEnter: 40,
  delayExit: 0,
  transformOriginList: 'top center',
  transformOriginModule: 'top center',
  // how far to translate vertically when hiding (px)
  translateY: 10,
  // horizontal shrink factor (1 = no shrink, 0.95 = 95% width)
  horizontalScale: 0.98,
  // how long to wait (ms) after mounting children before starting enter animation
  mountDelay: 50,
  // extra hold time (ms) after exit completes before shrinking height
  hold: 100
}

interface IProps {
  mode: EViewMode
  moduleNode: ReactNode
  listNode: ReactNode
  autoHeight?: boolean
  animConfig?: Partial<typeof DEFAULT_ANIM>
}

const AnimationSwitch: FC<IProps> = (props) => {
  // refs to measure heights
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const listRef = useRef<HTMLDivElement | null>(null)
  const moduleRef = useRef<HTMLDivElement | null>(null)
  // animation config merged from defaults + props
  const anim = { ...DEFAULT_ANIM, ...(props.animConfig || {}) }

  // mountedMode: which pane is currently mounted (rendered). We sequence
  // transitions by hiding the currently mounted pane first, then swapping
  // to the requested mode after the exit animation finishes.
  const [mountedMode, setMountedMode] = useState<EViewMode>(props.mode)
  const [mountedOpen, setMountedOpen] = useState<boolean>(true)
  const pendingModeRef = useRef<EViewMode | null>(null)
  const exitTimerRef = useRef<number | null>(null)

  useEffect(() => {
    // if prop didn't change, nothing to do
    if (props.mode === mountedMode) return

    // if a swap is already pending, replace it
    if (exitTimerRef.current) {
      window.clearTimeout(exitTimerRef.current)
      exitTimerRef.current = null
    }

    // request a swap: hide the currently mounted pane first
    pendingModeRef.current = props.mode

    // lock the current wrapper height so it doesn't shrink immediately
    const currentH = wrapperRef.current?.offsetHeight
    if (wrapperRef.current && typeof currentH === 'number') {
      wrapperRef.current.style.height = `${currentH}px`
      wrapperRef.current.style.transition = `height ${anim.enter}ms ${anim.ease}`
    }

    setMountedOpen(false)

    // after exit duration + optional hold, swap mounted pane and show it
    const exitDur = anim.exit
    const totalDelay = exitDur + (anim.hold ?? 0)
    exitTimerRef.current = window.setTimeout(() => {
      const next = pendingModeRef.current
      pendingModeRef.current = null
      if (next != null) {
        // mount the next pane
        setMountedMode(next)

        // wait a frame for DOM to mount the new content, measure and animate height
        requestAnimationFrame(() => {
          const targetRef = next === EViewMode.List ? listRef : moduleRef
          const newH = targetRef.current?.offsetHeight ?? wrapperRef.current?.offsetHeight ?? 0

          // start enter animation for the new pane
          setMountedOpen(true)

          // animate wrapper height from previous to new
          if (wrapperRef.current) {
            // ensure we set a numeric height first (we set currentH earlier)
            wrapperRef.current.style.height = `${newH}px`
            // after the transition finishes, reset to auto
            window.setTimeout(() => {
              if (wrapperRef.current) wrapperRef.current.style.height = ''
            }, anim.enter)
          }
        })
      }
      exitTimerRef.current = null
    }, totalDelay)

    return () => {
      if (exitTimerRef.current) {
        window.clearTimeout(exitTimerRef.current)
        exitTimerRef.current = null
      }
    }
  }, [props.mode, mountedMode])

  const classes = useMemo(() => {
    const { root, pane, paneList, paneModule, autoHeight } = animationSwitchClasses
    const rootClasses = [root, props.autoHeight ? autoHeight : '']
    const paneListClasses = [pane, paneList]
    const paneModuleClasses = [pane, paneModule]
    return { root: rootClasses, paneList: paneListClasses, paneModule: paneModuleClasses }
  }, [props.mode, props.autoHeight])

  return (
    <AnimationSwitchStyled className={classes.root.join(' ')}>
      <div ref={wrapperRef} className={animationSwitchClasses.content}>
        {mountedMode === EViewMode.List && (
          <CombinedTransition
            open={mountedOpen}
            timeout={{ enter: DEFAULT_ANIM.enter, exit: DEFAULT_ANIM.exit }}
            mountDelay={anim.mountDelay}
            anim={anim}
            style={{
              transitionTimingFunction: DEFAULT_ANIM.ease,
              transitionDuration: `${DEFAULT_ANIM.enter}ms`,
              transitionDelay: `${mountedOpen ? anim.delayEnter : anim.delayExit}ms`,
              transformOrigin: anim.transformOriginList
            }}
          >
            <div ref={listRef} className={classes.paneList.join(' ')} aria-hidden={mountedMode !== EViewMode.List}>
              {props.listNode}
            </div>
          </CombinedTransition>
        )}

        {mountedMode === EViewMode.Module && (
          <CombinedTransition
            open={mountedOpen}
            timeout={{ enter: DEFAULT_ANIM.enter, exit: DEFAULT_ANIM.exit }}
            mountDelay={anim.mountDelay}
            anim={anim}
            style={{
              transitionTimingFunction: DEFAULT_ANIM.ease,
              transitionDuration: `${DEFAULT_ANIM.enter}ms`,
              transitionDelay: `${mountedOpen ? anim.delayEnter : anim.delayExit}ms`,
              transformOrigin: anim.transformOriginModule
            }}
          >
            <div ref={moduleRef} className={classes.paneModule.join(' ')} aria-hidden={mountedMode !== EViewMode.Module}>
              {props.moduleNode}
            </div>
          </CombinedTransition>
        )}
      </div>
    </AnimationSwitchStyled>
  )
}
export default AnimationSwitch

export const animationSwitchClasses = {
  root: 'AnimationSwitch-root',
  content: 'AnimationSwitch-content',
  autoHeight: 'AnimationSwitch-auto-height',
  pane: 'AnimationSwitch-pane',
  paneList: 'AnimationSwitch-pane-list',
  paneModule: 'AnimationSwitch-pane-module'
}

const getClasses = (key: keyof typeof animationSwitchClasses, options?: { prefix?: string; suffix?: string }) => {
  return `${options?.prefix || ''}.${animationSwitchClasses[key]}${options?.suffix || ''}`
}

export const AnimationSwitchStyled = styled('div')(({ theme }) => ({
  [getClasses('root', { prefix: '&' })]: { width: '100%' },
  [getClasses('content')]: {
    position: 'relative',
    width: '100%'
  },
  // autoHeight mode
  // [getClasses('autoHeight', { prefix: '&' })]: { overflow: 'clip' },
  [`&:not(${getClasses('autoHeight')})`]: {
    height: '100%',
    [getClasses('content')]: { height: '100%' },
    [getClasses('pane')]: { height: '100%', display: 'flex' }
  }
}))

// Custom Transition-like component that mounts children immediately
// then waits `mountDelay` before starting the enter animation. On close,
// it runs the exit animation and then unmounts after exit duration.
const CombinedTransition: FC<{
  open: boolean
  timeout?: number | { enter?: number; exit?: number }
  mountDelay?: number
  style?: CSSProperties
  anim?: typeof DEFAULT_ANIM
  children: ReactElement
}> = ({ open, timeout, mountDelay, style, anim, children }) => {
  const cfg = anim ?? DEFAULT_ANIM
  const enterDur = typeof timeout === 'number' ? timeout : timeout?.enter ?? cfg.enter
  const exitDur = typeof timeout === 'number' ? timeout : timeout?.exit ?? cfg.exit
  const delayMount = mountDelay ?? cfg.mountDelay

  const [mounted, setMounted] = useState(open)
  const [showing, setShowing] = useState(open)
  const mountTimer = useRef<number | null>(null)
  const unmountTimer = useRef<number | null>(null)

  useEffect(() => {
    // open -> mount immediately, then wait mountDelay before starting enter
    if (open) {
      if (unmountTimer.current) {
        window.clearTimeout(unmountTimer.current)
        unmountTimer.current = null
      }
      setMounted(true)
      // delay the start of the enter animation so inner children can render
      if (delayMount > 0) {
        if (mountTimer.current) window.clearTimeout(mountTimer.current)
        mountTimer.current = window.setTimeout(() => {
          setShowing(true)
          mountTimer.current = null
        }, delayMount)
      } else {
        setShowing(true)
      }
      return
    }

    // close -> start exit immediately, then unmount after exitDur
    setShowing(false)
    if (mountTimer.current) {
      window.clearTimeout(mountTimer.current)
      mountTimer.current = null
    }
    if (unmountTimer.current) window.clearTimeout(unmountTimer.current)
    unmountTimer.current = window.setTimeout(() => {
      setMounted(false)
      unmountTimer.current = null
    }, exitDur)
    return
  }, [open, delayMount, enterDur, exitDur])

  useEffect(() => {
    return () => {
      if (mountTimer.current) window.clearTimeout(mountTimer.current)
      if (unmountTimer.current) window.clearTimeout(unmountTimer.current)
    }
  }, [])

  if (!mounted) return null

  const baseStyle: CSSProperties = {
    transitionProperty: 'opacity, transform',
    transitionTimingFunction: cfg.ease,
    transitionDuration: `${showing ? enterDur : exitDur}ms`,
    transitionDelay: `${showing ? cfg.delayEnter : cfg.delayExit}ms`,
    transformOrigin: style?.transformOrigin || cfg.transformOriginList
  }

  const visibleStyle: CSSProperties = { opacity: 1, transform: `translateY(0) scaleX(1)` }
  const hiddenStyle: CSSProperties = { opacity: 0, transform: `translateY(${cfg.translateY}px) scaleX(${cfg.horizontalScale})` }

  const styleObj: CSSProperties = { ...baseStyle, ...(showing ? visibleStyle : hiddenStyle), ...style }

  return cloneElement(children, { style: styleObj })
}
